import {
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CircleNotificationsOutlinedIcon from "@mui/icons-material/CircleNotificationsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "../../common-components/Icons/WhatsAppIcon";

export const SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#046287" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    bgcolor: "#046287",
  },
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": { color: "#9ca3af" },
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
    bgcolor: "#d1d5db",
    opacity: 1,
  },
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      px: 2,
      pt: 1.5,
      pb: 0.5,
      display: "block",
      fontWeight: 700,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

export const NotifToggle = ({
  icon: Icon,
  iconColor = "#046287",
  title,
  desc,
  checked,
  onChange,
  disabled,
}) => (
  <ListItem sx={{ py: 0.75 }}>
    <ListItemIcon sx={{ minWidth: 40 }}>
      <Icon sx={{ fontSize: 20, color: iconColor }} />
    </ListItemIcon>
    <ListItemText
      primary={title}
      secondary={desc}
      primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
      secondaryTypographyProps={{ fontSize: 11 }}
    />
    <Switch
      checked={checked}
      onChange={onChange ? (e) => onChange(e.target.checked) : undefined}
      disabled={disabled}
      size="small"
      sx={SWITCH_SX}
    />
  </ListItem>
);

export const NotificationPreferencesSection = ({
  notifPrefs,
  onUpdateEvent,
  onUpdateChannel,
  isPending,
  whatsappEnabled,
  smsNotificationsEnabled,
  emailNotificationsEnabled,
  userEmail,
  isEmailVerified,
}) => {
  const notifEvents = notifPrefs.events || {};
  const notifChannels = notifPrefs.channels || {};

  const emailDesc = !userEmail
    ? "No email added. Add one in the Ngwaahia app."
    : !isEmailVerified
      ? "Email not verified. Verify it in the Ngwaahia app."
      : "Sent as a fallback";

  return (
    <>
      <Divider sx={{ mx: 2 }} />
      <SectionLabel>Events</SectionLabel>
      <NotifToggle
        icon={LocalShippingOutlinedIcon}
        title="Order updates"
        desc="Status changes on your orders"
        checked={notifEvents.orderStatusChanged !== false}
        onChange={(v) => onUpdateEvent("orderStatusChanged", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={CheckCircleOutlineIcon}
        title="Order completed"
        desc="When your order is marked fulfilled"
        checked={notifEvents.orderFulfilled !== false}
        onChange={(v) => onUpdateEvent("orderFulfilled", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={PaymentOutlinedIcon}
        title="Payment updates"
        desc="Confirmation when your payment is accepted"
        checked={notifEvents.paymentStatusChanged !== false}
        onChange={(v) => onUpdateEvent("paymentStatusChanged", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={ShoppingCartOutlinedIcon}
        title="New shop orders"
        desc="When a customer places an order in your shop"
        checked={notifEvents.newOrder !== false}
        onChange={(v) => onUpdateEvent("newOrder", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={InventoryOutlinedIcon}
        title="Product out of stock"
        desc="When a product is auto-delisted due to sold-out stock"
        checked={notifEvents.productOutOfStock !== false}
        onChange={(v) => onUpdateEvent("productOutOfStock", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={ReportProblemOutlinedIcon}
        title="Buyer disputes"
        desc="When a buyer raises a dispute on your order"
        checked={notifEvents.buyerDisputed !== false}
        onChange={(v) => onUpdateEvent("buyerDisputed", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={CancelOutlinedIcon}
        title="Order cancellations"
        desc="When a buyer cancels an order in your shop"
        checked={notifEvents.buyerCancelled !== false}
        onChange={(v) => onUpdateEvent("buyerCancelled", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={AccountBalanceOutlinedIcon}
        title="Payment claims"
        desc="When a buyer uploads payment proof"
        checked={notifEvents.paymentClaimed !== false}
        onChange={(v) => onUpdateEvent("paymentClaimed", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={PersonAddOutlinedIcon}
        title="New personal followers"
        desc="When someone starts following you"
        checked={notifEvents.newUserFollower !== false}
        onChange={(v) => onUpdateEvent("newUserFollower", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={StoreOutlinedIcon}
        title="New shop followers"
        desc="When someone starts following your shop"
        checked={notifEvents.newShopFollower !== false}
        onChange={(v) => onUpdateEvent("newShopFollower", v)}
        disabled={isPending}
      />
      <NotifToggle
        icon={ShieldOutlinedIcon}
        title="Account & security"
        desc="Password resets and account changes"
        checked={
          notifEvents.passwordReset !== false &&
          notifEvents.accountChanged !== false
        }
        onChange={(v) => {
          onUpdateEvent("passwordReset", v);
          onUpdateEvent("accountChanged", v);
        }}
        disabled={isPending}
      />

      <Divider sx={{ mx: 2, mt: 1 }} />
      <SectionLabel>Channels</SectionLabel>
      <Typography
        variant="caption"
        sx={{ px: 2, pb: 0.5, display: "block", color: "text.secondary" }}
      >
        Delivered in this order:
      </Typography>
      <NotifToggle
        icon={CircleNotificationsOutlinedIcon}
        title="Push notifications"
        desc="To disable, go to your device's app settings"
        checked={true}
        onChange={null}
        disabled={true}
      />
      {whatsappEnabled && (
        <NotifToggle
          icon={WhatsAppIcon}
          title="WhatsApp"
          desc="Sent when you have WhatsApp on your number"
          checked={true}
          onChange={null}
          disabled={true}
        />
      )}
      {emailNotificationsEnabled && (
        <NotifToggle
          icon={EmailOutlinedIcon}
          title="Email"
          desc={emailDesc}
          checked={!!userEmail && !!isEmailVerified}
          onChange={null}
          disabled={true}
        />
      )}
      {smsNotificationsEnabled && (
        <NotifToggle
          icon={SmsOutlinedIcon}
          title="SMS alerts"
          desc="Carrier rates may apply"
          checked={!!notifChannels.sms}
          onChange={(v) => onUpdateChannel("sms", v)}
          disabled={isPending}
        />
      )}
    </>
  );
};
